import { Button } from "../Button/index.js";
import { ScrollArea } from "../ScrollArea/index.js";
import { Link } from "../Link/index.js";
import { ASULogo, GGELogo, GGEName, RZDLogo } from "../logos/index.js";
import { getSystemType, matchPath } from "./Navbar.lib.js";
import { SectionGroup } from "./local-components/SectionGroup.js";
import React from "react";
import { IconArrowLeft } from "@synthesis/shared-icons";
import { cn } from "@synthesis/shared-utils";
import { jsx, jsxs } from "react/jsx-runtime";
import { useRouterState } from "@tanstack/react-router";
//#region src/Navbar/index.tsx
const COLLAPSED_SIZE = 100;
const EXPANDED_SIZE = 270;
const iconLogoBySystem = {
	GGE: GGELogo,
	RZD: RZDLogo,
	EKPTD: RZDLogo,
	RAD: RZDLogo
};
const iconNameBySystem = {
	GGE: GGEName,
	RZD: ASULogo,
	EKPTD: ASULogo,
	RAD: ASULogo
};
const logoPositionClass = {
	collapsed: "absolute top-0 min-h-[30px] min-w-[50px] w-[50px]",
	GGE: "absolute left-4 min-h-[36px] min-w-[50px] w-[60px]",
	RZD: "top-0 min-h-[26px] min-w-[50px] w-[60px]",
	EKPTD: "top-0 min-h-[26px] min-w-[50px] w-[60px]",
	RAD: "top-0 min-h-[26px] min-w-[50px] w-[60px]"
};
const namePositionClass = {
	collapsed: "absolute bottom-0 left-5 w-[60px]",
	GGE: "absolute bottom-4 left-[80px] w-[160px]",
	RZD: "absolute bottom-4 left-[135px] w-[100px]",
	EKPTD: "absolute bottom-4 left-[135px] w-[100px]",
	RAD: "absolute bottom-4 left-[135px] w-[100px]"
};
const Navbar = ({ groups = [], isLoadingDescription, linksWhichBlockSideBar = [], handleGetIsUniqLink, className, ...rest }) => {
	const pathname = useRouterState({ select: (state) => state.location.pathname });
	const systemType = getSystemType();
	const LogoComponent = iconLogoBySystem[systemType];
	const ProjectNameComponent = iconNameBySystem[systemType];
	const [isCollapsedNavBar, setIsCollapsedNavBar] = React.useState(() => localStorage.getItem("collapsed-menu") === "true");
	const [isBlockedSideBar, setIsBlockedSideBar] = React.useState(false);
	const handleCollapsedNavBar = () => {
		localStorage.setItem("collapsed-menu", String(!isCollapsedNavBar));
		setIsCollapsedNavBar(!isCollapsedNavBar);
	};
	React.useLayoutEffect(() => {
		document.documentElement.style.setProperty("--sidebar-width", isCollapsedNavBar ? `${COLLAPSED_SIZE}px` : `${EXPANDED_SIZE}px`);
	}, [isCollapsedNavBar]);
	React.useEffect(() => {
		const match = linksWhichBlockSideBar.map((link) => matchPath({
			path: link,
			end: true
		}, pathname)).find((item) => item !== null);
		let isMatching = Boolean(match);
		if (match?.pathname.startsWith("/ontology/sfo/")) isMatching = match.params.rowId !== "list";
		if (isMatching) {
			setIsCollapsedNavBar(true);
			setIsBlockedSideBar(true);
			return;
		}
		setIsCollapsedNavBar(localStorage.getItem("collapsed-menu") === "true");
		setIsBlockedSideBar(false);
	}, [linksWhichBlockSideBar, pathname]);
	const sectionGroups = groups.map((group) => /* @__PURE__ */ jsx(SectionGroup, {
		isLoadingDescription,
		group,
		isCollapsedNavBar,
		handleGetIsUniqLink
	}, group.id));
	const isCollapsed = isCollapsedNavBar;
	const logoClass = isCollapsed ? logoPositionClass.collapsed : logoPositionClass[systemType];
	const nameClass = isCollapsed ? namePositionClass.collapsed : namePositionClass[systemType];
	return /* @__PURE__ */ jsx("aside", {
		className: cn("pr-[--sidebar-width]", className),
		...rest,
		children: /* @__PURE__ */ jsxs("div", {
			className: cn("bg-neutrals-95 fixed top-0 z-[100] flex h-screen w-[--sidebar-width] flex-shrink-0 flex-col py-2 duration-200 ease-in-out", isBlockedSideBar && "bg-transparent"),
			children: [
				/* @__PURE__ */ jsx(Button, {
					"data-cy": "navbar-buttonCollapse-navigation",
					theme: "elevated",
					className: cn("absolute -right-4 top-3.5 z-50 h-8 w-8 duration-100", isBlockedSideBar && "invisible"),
					onClick: handleCollapsedNavBar,
					icon: /* @__PURE__ */ jsx(IconArrowLeft, { className: cn("text-neutrals-30 size-4 transition-transform", isCollapsed && "rotate-180") })
				}),
				/* @__PURE__ */ jsx(Link, {
					"data-cy": "navbar-link-projectsSection",
					to: "/",
					className: "w-full no-underline decoration-transparent",
					children: /* @__PURE__ */ jsxs("section", {
						className: cn("relative flex h-12 w-full items-center overflow-hidden duration-200 ease-in-out", isCollapsed ? "px-[23px]" : "px-8"),
						children: [
							/* @__PURE__ */ jsx(LogoComponent, { className: cn("duration-200 ease-in-out", logoClass) }),
							systemType !== "GGE" ? /* @__PURE__ */ jsx("figure", {
								className: cn("bg-neutrals-90 ml-5 duration-200 ease-in-out", isCollapsed ? "h-0 w-0" : "h-full w-px"),
								"aria-hidden": true
							}) : null,
							/* @__PURE__ */ jsx(ProjectNameComponent, { className: cn("absolute duration-200 ease-in-out", nameClass) })
						]
					})
				}),
				!isBlockedSideBar && groups.length > 0 ? /* @__PURE__ */ jsx(ScrollArea, {
					direction: "vertical",
					className: "mt-3 h-[calc(100vh-50px)] pt-1",
					children: sectionGroups
				}) : null
			]
		})
	});
};
//#endregion
export { Navbar };

//# sourceMappingURL=index.js.map